// =============================================================================
// Web PubSub Hook - ACDE Frontend
// Real-time updates for process graphs with production-ready error handling
// =============================================================================

import { useState, useEffect, useCallback, useRef } from 'react';
import type { PubSubEvent, ProcessGraph, SIPOCData } from '@acde/shared';
import { useAuthFetch } from '@/hooks/useAuthFetch';
import { useAuth } from '@/contexts/AuthContext';

const API_URL = import.meta.env.VITE_API_URL || '/api';

interface SessionStatusData {
  status: string;
  metadata?: {
    processName?: string;
    completionPercentage?: number;
  };
}

interface UsePubSubOptions {
  sessionId: string;
  onGraphUpdate?: (graph: ProcessGraph) => void;
  onSIPOCUpdate?: (sipoc: SIPOCData) => void;
  onPhaseChange?: (phase: string) => void;
  onSessionStatus?: (data: SessionStatusData) => void;
  enabled?: boolean;
}

interface UsePubSubReturn {
  isConnected: boolean;
  isConnecting: boolean;
  error: string | null;
  reconnect: () => void;
  disconnect: () => void;
}

const MAX_RECONNECT_ATTEMPTS = 5;
const INITIAL_RECONNECT_DELAY = 1000;
const MAX_RECONNECT_DELAY = 30000;
const CONNECTION_DEBOUNCE_MS = 100;

export function usePubSub(options: UsePubSubOptions): UsePubSubReturn {
  const {
    sessionId,
    onGraphUpdate,
    onSIPOCUpdate,
    onPhaseChange,
    onSessionStatus,
    enabled = true,
  } = options;

  const { authFetch } = useAuthFetch();
  const { isReady } = useAuth();

  const [isConnected, setIsConnected] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Refs for connection management
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const connectionDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const reconnectAttemptsRef = useRef(0);
  const isMountedRef = useRef(true);
  const intentionalDisconnectRef = useRef(false);
  const connectionIdRef = useRef(0);
  const isConnectingRef = useRef(false);

  // Store callbacks in refs to avoid reconnection on callback changes
  const callbacksRef = useRef({
    onGraphUpdate,
    onSIPOCUpdate,
    onPhaseChange,
    onSessionStatus,
  });

  // Update callback refs when they change
  useEffect(() => {
    callbacksRef.current = {
      onGraphUpdate,
      onSIPOCUpdate,
      onPhaseChange,
      onSessionStatus,
    };
  }, [onGraphUpdate, onSIPOCUpdate, onPhaseChange, onSessionStatus]);

  // Handle incoming messages with Azure Web PubSub protocol
  const handleMessage = useCallback((event: MessageEvent) => {
    try {
      const rawMessage = JSON.parse(event.data);
      const callbacks = callbacksRef.current;

      // Handle Azure Web PubSub protocol messages
      switch (rawMessage.type) {
        case 'system':
          // System messages like connected, disconnected
          if (rawMessage.event === 'connected') {
            console.log('[PubSub] System connected, connectionId:', rawMessage.connectionId);
          }
          return;

        case 'ack':
          // Acknowledgment for joinGroup, etc.
          if (rawMessage.success) {
            console.log('[PubSub] Action acknowledged:', rawMessage.ackId);
          } else {
            console.warn('[PubSub] Action failed:', rawMessage.error);
          }
          return;

        case 'message': {
          // Handle group messages and server messages
          const data = rawMessage.data;
          
          // The actual event data is in rawMessage.data
          if (!data) {
            console.log('[PubSub] Empty message data');
            return;
          }

          // Parse the inner message if it's a string
          const message: PubSubEvent = typeof data === 'string' ? JSON.parse(data) : data;

          // Only process messages for our session
          if (message.sessionId && message.sessionId !== sessionId) {
            return;
          }

          switch (message.type) {
            case 'graph:update':
              console.log('[PubSub] Graph update received');
              callbacks.onGraphUpdate?.(message.data as ProcessGraph);
              break;

            case 'sipoc:update':
              console.log('[PubSub] SIPOC update received');
              callbacks.onSIPOCUpdate?.(message.data as SIPOCData);
              break;

            case 'phase:change':
              console.log('[PubSub] Phase change received');
              callbacks.onPhaseChange?.((message.data as { phase: string }).phase);
              break;

            case 'session:status':
              console.log('[PubSub] Session status received');
              callbacks.onSessionStatus?.(message.data as SessionStatusData);
              break;

            default:
              console.log('[PubSub] Unknown event type:', message.type);
          }
          break;
        }

        default:
          console.log('[PubSub] Unknown protocol message type:', rawMessage.type);
      }
    } catch (err) {
      console.error('[PubSub] Message parse error:', err);
    }
  }, [sessionId]);

  // Cleanup function for connection resources
  const cleanupConnection = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }

    if (connectionDebounceRef.current) {
      clearTimeout(connectionDebounceRef.current);
      connectionDebounceRef.current = null;
    }

    if (wsRef.current) {
      const ws = wsRef.current;
      wsRef.current = null;
      
      // Remove handlers before closing to avoid triggering reconnection
      ws.onopen = null;
      ws.onmessage = null;
      ws.onerror = null;
      ws.onclose = null;
      
      if (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING) {
        ws.close(1000, 'Cleanup');
      }
    }
  }, []);

  // Connect to PubSub with connection ID tracking
  const connect = useCallback(async (connectionId: number) => {
    // Bail out if this connection attempt is stale
    if (connectionId !== connectionIdRef.current) {
      return;
    }

    if (!enabled || !sessionId || !isReady) {
      return;
    }

    // Check if already connecting or connected
    if (isConnectingRef.current || wsRef.current?.readyState === WebSocket.OPEN) {
      return;
    }

    // Close any existing connection
    cleanupConnection();

    isConnectingRef.current = true;
    setIsConnecting(true);
    setError(null);
    intentionalDisconnectRef.current = false;

    try {
      // Get WebSocket URL from API
      const response = await authFetch(`${API_URL}/pubsub/negotiate`);

      // Bail out if connection attempt is stale or component unmounted
      if (connectionId !== connectionIdRef.current || !isMountedRef.current) {
        isConnectingRef.current = false;
        setIsConnecting(false);
        return;
      }

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to negotiate PubSub connection');
      }

      const { url } = await response.json();

      // Bail out again after async operation
      if (connectionId !== connectionIdRef.current || !isMountedRef.current) {
        isConnectingRef.current = false;
        setIsConnecting(false);
        return;
      }

      // Create WebSocket connection with Azure Web PubSub subprotocol
      // The subprotocol enables client-side group joining and messaging
      const ws = new WebSocket(url, 'json.webpubsub.azure.v1');

      ws.onopen = () => {
        // Verify this is still the current connection
        if (connectionId !== connectionIdRef.current || !isMountedRef.current) {
          ws.close(1000, 'Stale connection');
          return;
        }

        console.log('[PubSub] Connected with subprotocol');
        wsRef.current = ws;
        isConnectingRef.current = false;
        setIsConnected(true);
        setIsConnecting(false);
        setError(null);
        reconnectAttemptsRef.current = 0;

        // Join session group using Azure Web PubSub protocol
        ws.send(
          JSON.stringify({
            type: 'joinGroup',
            group: sessionId,
            ackId: Date.now(), // Required for acknowledgment
          })
        );
      };

      ws.onmessage = handleMessage;

      ws.onerror = (event) => {
        console.error('[PubSub] WebSocket error:', event);
        if (isMountedRef.current && connectionId === connectionIdRef.current) {
          setError('Connection error');
        }
      };

      ws.onclose = (event) => {
        console.log('[PubSub] Disconnected', event.code, event.reason);

        // Only process if this is still the current connection
        if (connectionId !== connectionIdRef.current) {
          return;
        }

        if (!isMountedRef.current) {
          return;
        }

        isConnectingRef.current = false;
        setIsConnected(false);
        setIsConnecting(false);
        
        if (wsRef.current === ws) {
          wsRef.current = null;
        }

        // Don't reconnect if:
        // 1. This was an intentional disconnect
        // 2. Close code indicates normal closure (1000) with our cleanup message
        // 3. Component is disabled
        const isNormalCleanupClose = event.code === 1000 && 
          (event.reason === 'Cleanup' || event.reason === 'User disconnected' || event.reason === 'Stale connection');
        
        if (intentionalDisconnectRef.current || isNormalCleanupClose || !enabled) {
          return;
        }

        // Attempt reconnection with exponential backoff
        if (reconnectAttemptsRef.current < MAX_RECONNECT_ATTEMPTS) {
          const delay = Math.min(
            INITIAL_RECONNECT_DELAY * Math.pow(2, reconnectAttemptsRef.current),
            MAX_RECONNECT_DELAY
          );

          console.log(`[PubSub] Reconnecting in ${delay}ms (attempt ${reconnectAttemptsRef.current + 1}/${MAX_RECONNECT_ATTEMPTS})`);

          reconnectTimeoutRef.current = setTimeout(() => {
            if (isMountedRef.current && connectionId === connectionIdRef.current) {
              reconnectAttemptsRef.current++;
              connect(connectionId);
            }
          }, delay);
        } else {
          setError('Connection failed after multiple attempts. Click to retry.');
        }
      };

      // Store reference temporarily during connection
      // (will be moved to wsRef.current on successful open)
    } catch (err) {
      console.error('[PubSub] Connection failed:', err);

      if (isMountedRef.current && connectionId === connectionIdRef.current) {
        isConnectingRef.current = false;
        setIsConnecting(false);
        setError(err instanceof Error ? err.message : 'Connection failed');
        
        // Attempt reconnection on fetch error
        if (reconnectAttemptsRef.current < MAX_RECONNECT_ATTEMPTS) {
          const delay = Math.min(
            INITIAL_RECONNECT_DELAY * Math.pow(2, reconnectAttemptsRef.current),
            MAX_RECONNECT_DELAY
          );
          
          reconnectTimeoutRef.current = setTimeout(() => {
            if (isMountedRef.current && connectionId === connectionIdRef.current) {
              reconnectAttemptsRef.current++;
              connect(connectionId);
            }
          }, delay);
        }
      }
    }
  }, [enabled, sessionId, isReady, authFetch, handleMessage, cleanupConnection]);

  // Disconnect from PubSub
  const disconnect = useCallback(() => {
    intentionalDisconnectRef.current = true;
    connectionIdRef.current++; // Invalidate any pending connection attempts
    
    cleanupConnection();
    
    isConnectingRef.current = false;
    setIsConnected(false);
    setIsConnecting(false);
    reconnectAttemptsRef.current = 0;
  }, [cleanupConnection]);

  // Manual reconnect
  const reconnect = useCallback(() => {
    disconnect();
    
    // Small delay to ensure disconnect completes
    setTimeout(() => {
      if (isMountedRef.current) {
        connectionIdRef.current++;
        reconnectAttemptsRef.current = 0;
        setError(null);
        connect(connectionIdRef.current);
      }
    }, 50);
  }, [disconnect, connect]);

  // Connect on mount/sessionId change with debouncing
  useEffect(() => {
    isMountedRef.current = true;
    intentionalDisconnectRef.current = false;

    // Increment connection ID to create a new connection context
    const thisConnectionId = ++connectionIdRef.current;

    if (enabled && sessionId && isReady) {
      // Debounce connection to handle StrictMode double-mounting
      connectionDebounceRef.current = setTimeout(() => {
        if (isMountedRef.current) {
          connect(thisConnectionId);
        }
      }, CONNECTION_DEBOUNCE_MS);
    }

    return () => {
      isMountedRef.current = false;
      intentionalDisconnectRef.current = true;
      // Invalidate the connection we started by incrementing the global counter
      // This uses the captured thisConnectionId to know what we were tracking
      connectionIdRef.current = thisConnectionId + 1;
      cleanupConnection();
      isConnectingRef.current = false;
    };
  }, [sessionId, enabled, isReady, connect, cleanupConnection]);

  return {
    isConnected,
    isConnecting,
    error,
    reconnect,
    disconnect,
  };
}
